// Editor forms + preview renderers for the content section types (video / banner). Each form
// reads/writes the section's opaque contentConfig JSON (see contentSectionConfig). Rich text
// sections keep their CONTENT in 1–3 typed column cells stored in the section's headerCells (edited
// via the same cells UI as product-section headers/footers); the form here configures only their
// optional CTA button, which lives in contentConfig.
//
// The banner form mirrors the hero banner editor panel's UI (thumbnail strip + dashed add tile +
// BannerDetail for the selected entry) and the preview mirrors the hero carousel (crossfading
// slides + dots) — a banner section IS a rotating carousel on the shopper; merchants stack
// multiple banner sections if they want them stacked.

import React from 'react';
import { Callout, DefaultButton, DirectionalHint, Dropdown, getTheme, Icon, IconButton, IDropdownOption, SearchBox, Spinner, SpinnerSize, Stack, TextField, Toggle } from '@fluentui/react';
import { IStorefrontBanner, IStorefrontSection } from '../../../../models/Storefront';
import NumberField from '../../../Inputs/NumberField';
import CompactColorPicker from '../../../Inputs/CompactColorPicker';
import StorefrontColorPickerButton from '../StorefrontColorPickerButton';
import StorefrontBannerImageUploader from '../Banners/StorefrontBannerImageUploader';
import { BannerDetail } from '../Banners/StorefrontBannerEditorPanel';
import { BannerOverlay } from '../Banners/StorefrontBannerEditor';
import {
    bannerObjectFit,
    DEFAULT_CONTENT_BANNER_HEIGHT,
    DIVIDER_DEFAULTS,
    IBannerContentConfig,
    IContentBannerEntry,
    IDividerContentConfig,
    IRichTextButtonConfig,
    IRichTextContentConfig,
    ISingleProductContentConfig,
    IVideoContentConfig,
    parseBannerContentConfig,
    parseDividerConfig,
    parseRichTextConfig,
    parseSingleProductConfig,
    parseVideoConfig,
    serializeContentConfig,
    youTubeId,
} from './contentSectionConfig';
import { isNarrowWidth } from './sectionWidths';
import { ISearchElementStyle } from '../searchPageConfig';
import { ElementStyleControls } from '../styleControls';
import { styleTargetForKey } from '../stylesConfig';
import {
    useLazySearchAdminStorefrontBundlesQuery,
    useLazySearchAdminStorefrontProductsQuery,
    useLazySearchAdminStorefrontSavedSearchProductsQuery,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontBundleView, IStorefrontProductView } from '../../../../models/StorefrontProducts';
import { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';

const ACCENT = 'rgb(120, 156, 47)';

interface IContentFormProps {
    section: IStorefrontSection;
    onChange: (section: IStorefrontSection) => void;
}

// ---------- Video ----------
export const VideoContentForm: React.FC<IContentFormProps> = ({ section, onChange }) => {
    const config = parseVideoConfig(section.contentConfig);
    const update = (patch: Partial<IVideoContentConfig>) =>
        onChange({ ...section, contentConfig: serializeContentConfig({ ...config, ...patch }) });
    return (
        <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
            <DynamicSetting id={`storefront.sectionField.${section.id}.videoUrl`}>
                <TextField
                    label='Video URL'
                    value={config.url ?? ''}
                    onChange={(_, v) => update({ url: v || undefined })}
                    placeholder='https://www.youtube.com/watch?v=…'
                    description='Paste a YouTube link (watch, share, or embed) or a direct video file URL (.mp4 / .webm).'
                />
            </DynamicSetting>
        </Stack>
    );
};

// ---------- Rich Text ----------
// Rich text CONTENT is the section's headerCells (1–3 typed columns, edited in the preview); the
// only thing configured here is the optional CTA button, which lives in contentConfig so it commits
// immediately rather than riding the cells' temp-state/commit-on-close path.
const BUTTON_POSITION_OPTIONS: IDropdownOption[] = [
    { key: 'start', text: 'Before Content' },
    { key: 'end', text: 'After Content' },
];
const BUTTON_ALIGN_OPTIONS: IDropdownOption[] = [
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
];

export const RichTextContentForm: React.FC<IContentFormProps> = ({ section, onChange }) => {
    const config = parseRichTextConfig(section.contentConfig);
    const update = (patch: Partial<IRichTextContentConfig>) =>
        onChange({ ...section, contentConfig: serializeContentConfig({ ...config, ...patch }) });
    // Nested patch helpers, same shape as the banner panel's updateButton / updateButtonStyle.
    const updateButton = (patch: Partial<IRichTextButtonConfig>) => update({ button: { ...config.button, ...patch } });
    const updateButtonStyle = (patch: Partial<ISearchElementStyle>) => updateButton({ style: { ...config.button?.style, ...patch } });

    return (
        <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
            <DynamicSetting id={`storefront.sectionField.${section.id}.buttonLabel`}>
                <TextField
                    label='Button Label'
                    description='The button only shows when a label is set.'
                    value={config.button?.label ?? ''}
                    onChange={(_, v) => updateButton({ label: v || undefined })}
                />
            </DynamicSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.buttonUrl`}>
                <TextField
                    label='Button URL'
                    value={config.button?.url ?? ''}
                    onChange={(_, v) => updateButton({ url: v || undefined })}
                />
            </DynamicSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.buttonPlacement`}>
                <Dropdown
                    label='Button Placement'
                    options={BUTTON_POSITION_OPTIONS}
                    selectedKey={config.button?.position ?? 'end'}
                    // 'end' is the default — store undefined for it so unchanged sections stay clean.
                    onChange={(_, o) => updateButton({ position: o?.key === 'start' ? 'start' : undefined })}
                />
            </DynamicSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.buttonAlignment`}>
                <Dropdown
                    label='Button Alignment'
                    options={BUTTON_ALIGN_OPTIONS}
                    selectedKey={config.button?.align ?? 'left'}
                    // 'left' is the default — store undefined for it.
                    onChange={(_, o) => updateButton({ align: o?.key === 'left' ? undefined : (o?.key as IRichTextButtonConfig['align']) })}
                />
            </DynamicSetting>
            <ElementStyleControls
                value={config.button?.style}
                onChange={updateButtonStyle}
                includeText
                styleTarget={styleTargetForKey('buttonStyle')}
                // Names whose style these controls edit — the renderer is generic and cannot know.
                settingKeyPrefix={`storefront.sectionStyle.${section.id}.button`}
            />
        </Stack>
    );
};

// ---------- Divider ----------
const DIVIDER_LINE_STYLE_OPTIONS: IDropdownOption[] = [
    { key: 'solid', text: 'Solid' },
    { key: 'dashed', text: 'Dashed' },
    { key: 'none', text: 'None (spacer only)' },
];

export const DividerContentForm: React.FC<IContentFormProps> = ({ section, onChange }) => {
    const config = parseDividerConfig(section.contentConfig);
    const update = (patch: Partial<IDividerContentConfig>) =>
        onChange({ ...section, contentConfig: serializeContentConfig({ ...config, ...patch }) });
    const lineStyle = config.lineStyle ?? DIVIDER_DEFAULTS.lineStyle;
    return (
        <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
            <DynamicSetting id={`storefront.sectionField.${section.id}.dividerLineStyle`}>
                <Dropdown
                    label='Line style'
                    selectedKey={lineStyle}
                    options={DIVIDER_LINE_STYLE_OPTIONS}
                    onChange={(_, o) => update({ lineStyle: o?.key as IDividerContentConfig['lineStyle'] })}
                />
            </DynamicSetting>
            <ConditionalSetting
                when={lineStyle !== 'none'}
                idPrefix={[
                    `storefront.sectionField.${section.id}.dividerThickness`,
                    `storefront.sectionField.${section.id}.dividerColor`,
                ]}
            >
                <DynamicSetting id={`storefront.sectionField.${section.id}.dividerThickness`}>
                    <NumberField label='Thickness' value={config.thickness ?? DIVIDER_DEFAULTS.thickness} onChange={(v) => update({ thickness: v })} suffix='px' />
                </DynamicSetting>
                <DynamicSetting id={`storefront.sectionField.${section.id}.dividerColor`}>
                    <CompactColorPicker label='Color' color={config.color ?? ''} onChange={(v) => update({ color: v || undefined })} />
                </DynamicSetting>
                <div style={{ fontSize: 12, color: '#605e5c' }}>
                    Leave the color unset for a subtle neutral line.
                </div>
            </ConditionalSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.dividerWidth`}>
                <NumberField label='Width' value={config.widthPercent ?? DIVIDER_DEFAULTS.widthPercent} onChange={(v) => update({ widthPercent: v })} suffix='%' />
            </DynamicSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.dividerSpacing`}>
                <NumberField label='Vertical spacing' value={config.spacing ?? DIVIDER_DEFAULTS.spacing} onChange={(v) => update({ spacing: v })} suffix='px' />
            </DynamicSetting>
        </Stack>
    );
};

// ---------- Banner ----------
export const BannerContentForm: React.FC<IContentFormProps> = ({ section, onChange }) => {
    const config = parseBannerContentConfig(section.contentConfig);
    const banners = config.banners ?? [];

    // Thumbnail-strip selection + drag reorder, mirroring StorefrontBannerEditorPanel.
    const [selectedIndex, setSelectedIndex] = React.useState(banners.length > 0 ? 0 : -1);
    const [dragIndex, setDragIndex] = React.useState<number | null>(null);
    const [dropIndex, setDropIndex] = React.useState<number | null>(null);

    const commit = (patch: Partial<IBannerContentConfig>) =>
        onChange({ ...section, contentConfig: serializeContentConfig({ ...config, ...patch }) });
    const commitBanners = (next: IContentBannerEntry[]) => commit({ banners: next });

    // Map a content banner entry to/from an IStorefrontBanner so the full overlay editor (BannerDetail)
    // can edit it. Only the contract fields (plus disabled, for the Enabled toggle) round-trip.
    const asBanner = (entry: IContentBannerEntry): IStorefrontBanner => ({
        imageUrl: entry.imageUrl,
        linkUrl: entry.linkUrl,
        altText: entry.altText,
        backgroundColor: entry.backgroundColor,
        overlayConfig: entry.overlayConfig,
        disabled: entry.disabled,
    });
    const fromBanner = (b: IStorefrontBanner): IContentBannerEntry => ({
        imageUrl: b.imageUrl,
        linkUrl: b.linkUrl,
        altText: b.altText,
        backgroundColor: b.backgroundColor,
        overlayConfig: b.overlayConfig,
        disabled: b.disabled,
    });

    const updateEntry = (index: number, entry: IContentBannerEntry) =>
        commitBanners(banners.map((e, i) => (i === index ? entry : e)));
    const addEntry = (imageUrl?: string) => {
        commitBanners([...banners, imageUrl ? { imageUrl } : {}]);
        setSelectedIndex(banners.length);
    };
    const removeEntry = (index: number) => {
        commitBanners(banners.filter((_, i) => i !== index));
        setSelectedIndex((cur) => (cur === index ? -1 : cur > index ? cur - 1 : cur));
    };

    const onDrop = (index: number) => {
        if (dragIndex === null || dragIndex === index) {
            setDragIndex(null);
            setDropIndex(null);
            return;
        }
        const next = [...banners];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(index, 0, moved);
        commitBanners(next);
        setSelectedIndex(index);
        setDragIndex(null);
        setDropIndex(null);
    };

    // Same thumbnail geometry as the hero banner panel, scaled by the configured height.
    const thumbWidth = 108;
    const thumbHeight = Math.max(24, (config.height ?? DEFAULT_CONTENT_BANNER_HEIGHT) / 800 * thumbWidth * 2);

    const selectedEntry = selectedIndex >= 0 ? banners[selectedIndex] : undefined;

    return (
        <Stack tokens={{ childrenGap: 12 }} style={{ marginTop: 12 }}>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign='end'>
                <div style={{ width: 140 }}>
                    <DynamicSetting id={`storefront.sectionField.${section.id}.bannerHeight`}>
                        <NumberField label='Height' value={config.height} onChange={(v) => commit({ height: v })} suffix='px' />
                    </DynamicSetting>
                </div>
                <DynamicSetting id={`storefront.sectionField.${section.id}.bannerImageFit`}>
                    <Dropdown
                        label='Image fit'
                        placeholder='Fill (default)'
                        options={[
                            { key: 'fill', text: 'Fill (crop)' },
                            { key: 'fit', text: 'Fit (letterbox)' },
                        ]}
                        selectedKey={config.imageFit ?? null}
                        onChange={(_, o) => commit({ imageFit: (o?.key as 'fit' | 'fill') || undefined })}
                        styles={{ root: { width: 160 } }}
                    />
                </DynamicSetting>
            </Stack>
            <DynamicSetting id={`storefront.sectionField.${section.id}.bannerFullBleed`}>
                <Toggle
                    label='Full-bleed'
                    inlineLabel
                    onText='Full width'
                    offText='Page width'
                    checked={!!config.fullBleed}
                    onChange={(_, checked) => commit({ fullBleed: checked || undefined })}
                    styles={{ root: { marginBottom: 0 } }}
                />
            </DynamicSetting>

            {/* Thumbnail strip: click to select, drag to reorder, dashed tile to add. The banners
                rotate as one carousel on the page — add another Banner section to stack instead. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {banners.map((entry, index) => {
                    const isSelected = index === selectedIndex;
                    const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index;
                    return (
                        <div
                            key={index}
                            draggable
                            onDragStart={() => { setDragIndex(index); setDropIndex(index); }}
                            onDragEnter={() => setDropIndex(index)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => onDrop(index)}
                            onDragEnd={() => { setDragIndex(null); setDropIndex(null); }}
                            onClick={() => setSelectedIndex(index)}
                            title={entry.altText || `Banner ${index + 1}`}
                            style={{
                                width: thumbWidth,
                                height: thumbHeight,
                                borderRadius: 8,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative',
                                boxSizing: 'border-box',
                                border: `2px solid ${isSelected ? ACCENT : 'transparent'}`,
                                boxShadow: isDropTarget ? '0 0 0 3px rgba(120, 156, 47, 0.5)' : (isSelected ? undefined : '0 0 0 1px #d0d0d0'),
                                opacity: dragIndex === index ? 0.6 : (entry.disabled ? 0.5 : 1),
                                background: entry.backgroundColor || '#f3f5f7',
                            }}
                        >
                            {entry.imageUrl ? (
                                <img
                                    src={entry.imageUrl}
                                    alt={entry.altText || `Banner ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                                />
                            ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#605e5c', textAlign: 'center', padding: 4, boxSizing: 'border-box' }}>
                                    {`Banner ${index + 1}`}
                                </div>
                            )}
                        </div>
                    );
                })}
                <div
                    style={{
                        width: thumbWidth,
                        height: thumbHeight,
                        border: `2px dashed ${ACCENT}`,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                    }}
                >
                    <StorefrontBannerImageUploader
                        isNew
                        onChange={(url) => addEntry(url)}
                        width={thumbWidth}
                        height={thumbHeight}
                    />
                </div>
            </div>

            {selectedEntry ? (
                // Remount on selection change so BannerDetail's per-banner local state resets.
                <Stack key={selectedIndex} tokens={{ childrenGap: 6 }}>
                    <DynamicSetting id={`storefront.sectionField.${section.id}.bannerBackground`}>
                        <StorefrontColorPickerButton
                            label='Background'
                            entries={[{ label: 'Background', type: 'color', value: selectedEntry.backgroundColor, onChange: (v) => updateEntry(selectedIndex, { ...selectedEntry, backgroundColor: v }), onClear: () => updateEntry(selectedIndex, { ...selectedEntry, backgroundColor: undefined }) }]}
                            style={{ opacity: 1, position: 'static' }}
                        />
                    </DynamicSetting>
                    {/* Reuse the storefront banner overlay editor (image / alt / link / enabled +
                        Text / Button / Box / Position overlay tabs) for the selected entry. */}
                    <BannerDetail
                        banner={asBanner(selectedEntry)}
                        onChange={(b) => updateEntry(selectedIndex, { ...selectedEntry, ...fromBanner(b) })}
                        onDelete={() => removeEntry(selectedIndex)}
                    />
                </Stack>
            ) : (
                <div style={{ color: '#605e5c', fontSize: 13 }}>
                    Select a banner above to edit it, or use the dashed tile to add one.
                </div>
            )}
        </Stack>
    );
};

// ---------- Single Product ----------
// A merchant picks ONE item — product, bundle, or saved-search product — from their storefront to
// feature as a hero card. The pick is stored as itemType/itemId in contentConfig (+ optional
// blurb/ctaLabel); a snapshot of the display fields is cached alongside so the editor preview
// renders without a live product fetch (the shopper ignores it — the server resolves live).

type SingleProductItemType = NonNullable<ISingleProductContentConfig['itemType']>;

const KIND_LABEL: Record<SingleProductItemType, string> = {
    product: 'Product',
    bundle: 'Bundle',
    savedSearch: 'Saved Search',
};

const formatPrice = (n?: number): string =>
    n == null ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

// One flattened, ready-to-store candidate row (mirrors the snapshot shape + the itemType/itemId).
interface ISingleProductPick {
    itemType: SingleProductItemType;
    itemId: string;
    name?: string;
    imageUrl?: string;
    price?: number;
    manufacturer?: string;
    hidePrice?: boolean;
}

const productToPick = (p: IStorefrontProductView, itemType: 'product' | 'savedSearch'): ISingleProductPick => {
    const price = p.basePrice ?? p.msrp ?? undefined;
    return {
        itemType,
        // For a saved-search product the meaningful server identifier is the saved-search id; fall
        // back to the product id when the view doesn't carry one.
        itemId: (itemType === 'savedSearch' ? p.savedSearchId : undefined) ?? p.id,
        name: p.name,
        imageUrl: p.image ?? p.images?.[0],
        price,
        manufacturer: p.manufacturer,
        hidePrice: price == null,
    };
};

const bundleToPick = (b: IStorefrontBundleView): ISingleProductPick => ({
    itemType: 'bundle',
    itemId: b.id,
    name: b.name,
    imageUrl: b.image,
    price: b.price ?? undefined,
    hidePrice: b.price == null,
});

interface ISingleProductFormProps extends IContentFormProps {
    // The storefront being edited. May be a preview id (…-preview); the picker searches the LIVE
    // catalog, so we strip the suffix before querying.
    storefrontId?: string;
}

export const SingleProductContentForm: React.FC<ISingleProductFormProps> = ({ section, onChange, storefrontId }) => {
    const config = parseSingleProductConfig(section.contentConfig);
    const liveStorefrontId = (storefrontId ?? '').replace(/-preview$/, '');

    const update = (patch: Partial<ISingleProductContentConfig>) =>
        onChange({ ...section, contentConfig: serializeContentConfig({ ...config, ...patch }) });

    const [searchProducts] = useLazySearchAdminStorefrontProductsQuery();
    const [searchBundles] = useLazySearchAdminStorefrontBundlesQuery();
    const [searchSaved] = useLazySearchAdminStorefrontSavedSearchProductsQuery();

    const [term, setTerm] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [groups, setGroups] = React.useState<{ label: string; items: ISingleProductPick[] }[]>([]);
    const [changing, setChanging] = React.useState(false);
    const debounceRef = React.useRef<number>();
    const anchorRef = React.useRef<HTMLDivElement>(null);

    const runSearch = React.useCallback(async (raw: string) => {
        const q = raw.trim();
        if (!liveStorefrontId || q.length < 2) {
            setGroups([]);
            setOpen(false);
            return;
        }
        setLoading(true);
        setOpen(true);
        const conditions = { page: 1, pageSize: 5, searchTerm: q };
        const empty = { items: [] as never[] };
        const [products, bundles, saved] = await Promise.all([
            searchProducts({ storefrontId: liveStorefrontId, conditions }).unwrap().catch(() => empty),
            searchBundles({ storefrontId: liveStorefrontId, conditions }).unwrap().catch(() => empty),
            searchSaved({ storefrontId: liveStorefrontId, conditions }).unwrap().catch(() => empty),
        ]);
        setGroups([
            { label: 'Products', items: (products.items as IStorefrontProductView[]).map((p) => productToPick(p, 'product')) },
            { label: 'Bundles', items: (bundles.items as IStorefrontBundleView[]).map(bundleToPick) },
            { label: 'Saved Search Products', items: (saved.items as IStorefrontProductView[]).map((p) => productToPick(p, 'savedSearch')) },
        ].filter((g) => g.items.length > 0));
        setLoading(false);
    }, [liveStorefrontId, searchProducts, searchBundles, searchSaved]);

    const onSearchChange = (value?: string) => {
        setTerm(value ?? '');
        window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => runSearch(value ?? ''), 300);
    };

    React.useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    const pick = (r: ISingleProductPick) => {
        update({
            itemType: r.itemType,
            itemId: r.itemId,
            snapshot: { name: r.name, imageUrl: r.imageUrl, price: r.price, manufacturer: r.manufacturer, hidePrice: r.hidePrice },
        });
        setOpen(false);
        setChanging(false);
        setTerm('');
        setGroups([]);
    };

    const clearPick = () => update({ itemType: undefined, itemId: undefined, snapshot: undefined });

    const theme = getTheme();
    const snap = config.snapshot;
    const hasPick = !!config.itemId;
    const showPicker = !hasPick || changing;

    return (
        <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
            {showPicker ? (
                <div ref={anchorRef}>
                    <SearchBox
                        placeholder='Search products, bundles, saved searches…'
                        value={term}
                        onChange={(_, v) => onSearchChange(v)}
                        onClear={() => { setTerm(''); setGroups([]); setOpen(false); }}
                        disabled={!liveStorefrontId}
                    />
                    {!liveStorefrontId && (
                        <div style={{ fontSize: 12, color: theme.palette.neutralSecondary, marginTop: 4 }}>
                            Save the storefront first to search its catalog.
                        </div>
                    )}
                    {open && (
                        <Callout
                            target={anchorRef}
                            onDismiss={() => setOpen(false)}
                            isBeakVisible={false}
                            gapSpace={2}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            styles={{ root: { width: (anchorRef.current?.offsetWidth ?? 380) } }}
                        >
                            {loading ? (
                                <div style={{ padding: '1em' }}><Spinner size={SpinnerSize.small} label='Searching…' labelPosition='right' /></div>
                            ) : groups.length === 0 ? (
                                <div style={{ padding: '1em', color: theme.palette.neutralSecondary, fontSize: 13 }}>
                                    {term.trim().length < 2 ? 'Type at least 2 characters to search.' : 'No matches found.'}
                                </div>
                            ) : (
                                <div style={{ maxHeight: 320, overflowY: 'auto', padding: '0.25em 0' }}>
                                    {groups.map((group) => (
                                        <div key={group.label}>
                                            <div style={{ padding: '0.25em 0.75em', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: theme.palette.neutralSecondary }}>
                                                {group.label}
                                            </div>
                                            {group.items.map((item, idx) => (
                                                <div
                                                    key={`${item.itemType}-${item.itemId}-${idx}`}
                                                    onClick={() => pick(item)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.4em 0.75em', cursor: 'pointer' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.background = theme.palette.neutralLighter)}
                                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                                >
                                                    <PickThumb imageUrl={item.imageUrl} size={32} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name || '(untitled)'}</div>
                                                        {item.manufacturer && <div style={{ fontSize: 11, color: theme.palette.neutralSecondary }}>{item.manufacturer}</div>}
                                                    </div>
                                                    {!item.hidePrice && item.price != null && (
                                                        <div style={{ fontSize: 12, color: theme.palette.neutralPrimary }}>{formatPrice(item.price)}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Callout>
                    )}
                    {changing && (
                        <div style={{ marginTop: 6 }}>
                            <DefaultButton text='Cancel' onClick={() => { setChanging(false); setTerm(''); setGroups([]); setOpen(false); }} />
                        </div>
                    )}
                </div>
            ) : (
                // Chosen item as a mini card with Change / Clear affordances.
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', border: `1px solid ${theme.palette.neutralLight}`, borderRadius: 8, padding: '0.5em' }}>
                    <PickThumb imageUrl={snap?.imageUrl} size={48} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{snap?.name || '(untitled)'}</div>
                        <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 6px', borderRadius: 10, fontSize: 10, background: theme.palette.themeLighter, color: theme.palette.themeDarker }}>
                            {config.itemType ? KIND_LABEL[config.itemType] : ''}
                        </span>
                        {snap && (snap.hidePrice || snap.price == null
                            ? <div style={{ fontSize: 12, color: theme.palette.neutralSecondary, marginTop: 2 }}>Request a Quote</div>
                            : <div style={{ fontSize: 12, color: theme.palette.neutralPrimary, marginTop: 2 }}>{formatPrice(snap.price)}</div>)}
                    </div>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <IconButton iconProps={{ iconName: 'Edit' }} title='Change item' ariaLabel='Change item' onClick={() => { setChanging(true); setTerm(''); }} />
                        <IconButton iconProps={{ iconName: 'Delete' }} title='Clear item' ariaLabel='Clear item' onClick={clearPick} />
                    </Stack>
                </div>
            )}

            <DynamicSetting id={`storefront.sectionField.${section.id}.blurb`}>
                <TextField
                    label='Blurb (optional)'
                    multiline
                    rows={3}
                    value={config.blurb ?? ''}
                    onChange={(_, v) => update({ blurb: v || undefined })}
                    placeholder='Optional marketing copy shown alongside the featured item.'
                />
            </DynamicSetting>
            <DynamicSetting id={`storefront.sectionField.${section.id}.ctaButtonText`}>
                <TextField
                    label='Button text'
                    value={config.ctaLabel ?? ''}
                    onChange={(_, v) => update({ ctaLabel: v || undefined })}
                    placeholder='View Details'
                />
            </DynamicSetting>
        </Stack>
    );
};

// Small square thumbnail with a package fallback, shared by the picker rows, mini card, and preview.
export const PickThumb: React.FC<{ imageUrl?: string; size: number }> = ({ imageUrl, size }) => {
    const theme = getTheme();
    return (
        <div style={{ width: size, height: size, flex: `0 0 ${size}px`, borderRadius: 6, overflow: 'hidden', background: '#f3f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imageUrl
                ? <img src={imageUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                : <Icon iconName='Package' styles={{ root: { fontSize: size * 0.5, color: theme.palette.neutralTertiary } }} />}
        </div>
    );
};

// ---------- Preview ----------
// isDark is part of the call signature for parity with the other previews, but the remaining
// content previews (video iframe, banner carousel) render on their own backgrounds.
export const ContentSectionPreview: React.FC<{ section: IStorefrontSection; isDark?: boolean }> = ({ section }) => {
    switch (section.type) {
        case 'video':
            return <VideoPreview config={parseVideoConfig(section.contentConfig)} />;
        // richText renders through the section's headerCells (the shared cell editors/renderers)
        // rather than a contentConfig-driven preview.
        case 'banner':
            return <BannerContentPreview config={parseBannerContentConfig(section.contentConfig)} />;
        case 'singleProduct':
            // Narrow sections (a third or less) stack vertically (image on top); anything wider —
            // half, twoThirds, threeQuarters, full — keeps the image-left layout.
            return <SingleProductPreview config={parseSingleProductConfig(section.contentConfig)} vertical={isNarrowWidth(section.width)} />;
        case 'divider':
            return <DividerPreview config={parseDividerConfig(section.contentConfig)} />;
        default:
            return null;
    }
};

// Divider preview: a centered rule (or, when the line style is 'none', just vertical space). Mirrors
// the shopper — borderTop `${thickness}px ${lineStyle} ${color}`, width `${widthPercent}%`, centered,
// wrapped in `${spacing}px` of vertical padding. An unset color inherits the section's text color.
const DividerPreview: React.FC<{ config: IDividerContentConfig }> = ({ config }) => {
    const lineStyle = config.lineStyle ?? DIVIDER_DEFAULTS.lineStyle;
    const thickness = config.thickness ?? DIVIDER_DEFAULTS.thickness;
    const widthPercent = config.widthPercent ?? DIVIDER_DEFAULTS.widthPercent;
    const spacing = config.spacing ?? DIVIDER_DEFAULTS.spacing;
    return (
        <div style={{ width: '100%', padding: `${spacing}px 0`, boxSizing: 'border-box' }}>
            <div
                style={{
                    width: `${widthPercent}%`,
                    margin: '0 auto',
                    borderTop: lineStyle === 'none' ? undefined : `${thickness}px ${lineStyle} ${config.color || 'rgba(0,0,0,0.15)'}`,
                }}
            />
        </div>
    );
};

// Multi-line clamp matching the shopper hero's text clamping.
const previewClamp = (lines: number): React.CSSProperties => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical' as never,
    overflow: 'hidden',
});

// Hero preview rendered FROM SNAPSHOT ONLY (no live fetch) — the server resolves the live item on
// the shopper. Mirrors the shopper's SingleProductSection styling: a TRANSPARENT card that inherits
// the section's text color (so it sits naturally on the store's background), a translucent border,
// and a white pane behind the product image only. hidePrice (or a missing price) shows "Request a
// Quote" like the shopper does.
const SingleProductPreview: React.FC<{ config: ISingleProductContentConfig; vertical?: boolean }> = ({ config, vertical }) => {
    const theme = getTheme();
    const snap = config.snapshot;

    if (!config.itemId || !snap) {
        return (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5em', color: theme.palette.neutralSecondary, background: '#f3f5f7', borderRadius: 8, padding: '2em' }}>
                <Icon iconName='Package' styles={{ root: { fontSize: 32 } }} />
                <span>Choose a product to feature</span>
            </div>
        );
    }

    const cta = config.ctaLabel?.trim() || 'View Details';
    const priceText = snap.hidePrice || snap.price == null ? 'Request a Quote' : formatPrice(snap.price);

    const imagePane = (
        <div style={{ flex: vertical ? '0 0 auto' : '0 0 40%', width: vertical ? '100%' : undefined, minWidth: vertical ? undefined : 180, minHeight: vertical ? 180 : 200, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box' }}>
            {snap.imageUrl
                ? <img src={snap.imageUrl} alt={snap.name ?? ''} style={{ maxWidth: '100%', maxHeight: vertical ? 180 : 220, objectFit: 'contain', display: 'block' }} />
                : <Icon iconName='Package' styles={{ root: { fontSize: 48, color: theme.palette.neutralTertiary } }} />}
        </div>
    );

    const infoPane = (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.75em', padding: vertical ? '1em' : '1.25em 1.25em 1.25em 0' }}>
            {snap.manufacturer && (
                <span style={{ fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7 }}>{snap.manufacturer}</span>
            )}
            <span style={{ fontSize: '1.4em', fontWeight: 600, lineHeight: 1.2, ...previewClamp(vertical ? 3 : 2) }}>{snap.name || '(untitled)'}</span>
            <span style={{ fontSize: snap.hidePrice || snap.price == null ? '1.15em' : '1.25em', fontWeight: 600 }}>{priceText}</span>
            {config.blurb && <span style={{ opacity: 0.8, lineHeight: 1.4, ...previewClamp(3) }}>{config.blurb}</span>}
            <div>
                <span style={{ display: 'inline-block', background: ACCENT, color: '#fff', borderRadius: 4, padding: '0.5em 1em', fontSize: 13, fontWeight: 600 }}>{cta}</span>
            </div>
        </div>
    );

    return (
        <div
            style={{
                marginTop: 12,
                display: 'flex',
                flexDirection: vertical ? 'column' : 'row',
                gap: vertical ? 0 : '1.25em',
                alignItems: 'stretch',
                border: '1px solid rgba(127,127,127,0.25)',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                boxSizing: 'border-box',
            }}
        >
            {imagePane}
            {infoPane}
        </div>
    );
};

const VideoPreview: React.FC<{ config: IVideoContentConfig }> = ({ config }) => {
    const ytId = youTubeId(config.url);
    return (
        <div style={{ marginTop: 12 }}>
            {!config.url && <div style={{ color: '#605e5c', background: '#f3f5f7', borderRadius: 8, padding: '2em', textAlign: 'center' }}>Add a video URL to preview</div>}
            {config.url && (ytId ? (
                <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden' }}>
                    <iframe
                        title='Video'
                        src={`https://www.youtube.com/embed/${ytId}`}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                        allowFullScreen
                    />
                </div>
            ) : (
                <video src={config.url} controls style={{ width: '100%', borderRadius: 8, background: '#000' }} />
            ))}
        </div>
    );
};

// Rotating carousel preview mirroring the hero banner editor (crossfading slides, dot buttons,
// 5s auto-rotate paused on hover). Disabled entries are skipped, like the hero carousel.
const BannerContentPreview: React.FC<{ config: IBannerContentConfig }> = ({ config }) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [isPaused, setIsPaused] = React.useState(false);
    const activeBanners = (config.banners ?? []).filter((b) => b.disabled !== true);

    React.useEffect(() => {
        if (activeIndex >= activeBanners.length) setActiveIndex(0);
    }, [activeBanners.length, activeIndex]);

    React.useEffect(() => {
        if (activeBanners.length < 2 || isPaused) return;
        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % activeBanners.length);
        }, 5000);
        return () => window.clearInterval(timer);
    }, [activeBanners.length, isPaused]);

    if (activeBanners.length === 0) return <span style={{ color: '#605e5c' }}>Add a banner to preview</span>;
    const height = config.height ?? DEFAULT_CONTENT_BANNER_HEIGHT;

    return (
        <div style={{ marginTop: 12 }} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            <div
                style={{
                    position: 'relative',
                    height,
                    overflow: 'hidden',
                    // Full-bleed: square corners + break the carousel itself out to 100vw centered
                    // on the content column (the column sits centered in the clipping
                    // .storefront-editor canvas, whose overflow-x: clip trims the overhang), so the
                    // image spans edge-to-edge like the shopper's fullBleedBand — a box-shadow band
                    // can only extend the background color, not the image.
                    ...(config.fullBleed
                        ? { borderRadius: 0, width: '100vw', marginLeft: 'calc(50% - 50vw)' }
                        : { borderRadius: 8, width: '100%' }),
                    background: '#f3f5f7',
                }}
            >
                {activeBanners.map((entry, index) => {
                    const isActive = index === activeIndex;
                    return (
                        <div
                            key={index}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                opacity: isActive ? 1 : 0,
                                transform: isActive ? 'scale(1)' : 'scale(1.02)',
                                transition: 'opacity 500ms ease, transform 500ms ease',
                                pointerEvents: isActive ? 'auto' : 'none',
                                backgroundColor: entry.backgroundColor || '#f3f5f7',
                            }}
                        >
                            {entry.imageUrl && (
                                <img src={entry.imageUrl} alt={entry.altText ?? ''} style={{ width: '100%', height: '100%', objectFit: bannerObjectFit(config), display: 'block' }} />
                            )}
                            {/* No `editable`: PreviewCanvas cancels link navigation across the whole
                                preview now, so the overlay no longer opts into that per-instance. */}
                            <BannerOverlay banner={entry} />
                        </div>
                    );
                })}
            </div>
            {activeBanners.length > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
                    {activeBanners.map((_, index) => (
                        <button
                            key={index}
                            type='button'
                            onClick={() => setActiveIndex(index)}
                            aria-label={`Show banner ${index + 1}`}
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                border: 'none',
                                background: index === activeIndex ? '#0f6cbd' : '#b8c6d3',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
